import React, { useState, useEffect } from 'react';
import { storageService } from '../services/storageService';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';

interface AnalysisResult {
  name: string;
  score: number;
  comment: string;
  icon: string;
}

const STORAGE_KEY_CONTENT = 'algo_analyzer_content';
const STORAGE_KEY_RESULTS = 'algo_analyzer_results';

export const AlgoAnalyzer: React.FC = () => {
  const { t } = useT();
  const [content, setContent] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY_CONTENT) || '';
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState<AnalysisResult[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_RESULTS);
    if (!saved) return [];
    try { return JSON.parse(saved); } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_CONTENT, content);
  }, [content]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_RESULTS, JSON.stringify(results));
  }, [results]);

  const handleAnalyze = async () => {
    if (!content.trim()) return;

    setIsAnalyzing(true);

    try {
      const { callAI } = await import('../services/aiService');
      const prompt = `${t('algoAnalyzer.systemPrompt.instruction')}

${t('algoAnalyzer.systemPrompt.contentLabel')}
${content}

${t('algoAnalyzer.systemPrompt.metricsLabel')}
1. hook_strength: ${t('algoAnalyzer.systemPrompt.hookStrength')}
2. clarity: ${t('algoAnalyzer.systemPrompt.clarity')}
3. engagement: ${t('algoAnalyzer.systemPrompt.engagement')}
4. curiosity: ${t('algoAnalyzer.systemPrompt.curiosity')}
5. emotional: ${t('algoAnalyzer.systemPrompt.emotional')}
6. shareability: ${t('algoAnalyzer.systemPrompt.shareability')}
7. trend_alignment: ${t('algoAnalyzer.systemPrompt.trendAlignment')}
8. readability: ${t('algoAnalyzer.systemPrompt.readability')}

${t('algoAnalyzer.systemPrompt.formatLabel')}
{
  "metrics": [
    { "key": "hook_strength", "score": 8, "comment": "..." },
    ...
  ]
}`;

      const jsonText = await callAI(
        [
          { role: 'system', content: t('algoAnalyzer.systemPrompt.systemMessage') },
          { role: 'user', content: prompt }
        ],
        { temperature: 0.5, maxTokens: 1500, jsonMode: true }
      );

      const parsed = JSON.parse(jsonText);
      const metrics = parsed.metrics || [];

      const metricNames: Record<string, { name: string; icon: string }> = {
        hook_strength: { name: t('algoAnalyzer.metrics.hookStrength'), icon: 'H' },
        clarity: { name: t('algoAnalyzer.metrics.clarity'), icon: 'C' },
        engagement: { name: t('algoAnalyzer.metrics.engagement'), icon: 'E' },
        curiosity: { name: t('algoAnalyzer.metrics.curiosity'), icon: '?' },
        emotional: { name: t('algoAnalyzer.metrics.emotional'), icon: 'Em' },
        shareability: { name: t('algoAnalyzer.metrics.shareability'), icon: 'S' },
        trend_alignment: { name: t('algoAnalyzer.metrics.trendAlignment'), icon: 'T' },
        readability: { name: t('algoAnalyzer.metrics.readability'), icon: 'R' },
      };

      const analysisResults: AnalysisResult[] = metrics.map((m: any) => ({
        name: metricNames[m.key]?.name || m.key,
        score: Math.max(0, Math.min(10, Number(m.score) || 0)),
        comment: m.comment || '',
        icon: metricNames[m.key]?.icon || '-',
      }));

      if (analysisResults.length === 0) {
        throw new Error(t('algoAnalyzer.noResults'));
      }

      setResults(analysisResults);

      const avgScore = analysisResults.reduce((sum, r) => sum + r.score, 0) / analysisResults.length;
      storageService.addHistoryItem({
        type: 'analysis',
        content: content.slice(0, 800),
        score: Math.round(avgScore * 10) / 10,
        metadata: { metricsCount: analysisResults.length },
      });
    } catch (error) {
      console.error('[AlgoAnalyzer] Analysis failed:', error);
      setResults([{
        name: t('algoAnalyzer.error'),
        score: 0,
        comment: error instanceof Error ? error.message : t('algoAnalyzer.errorMessage'),
        icon: '!',
      }]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const overallScore = results.length > 0
    ? Math.round(results.reduce((sum, r) => sum + r.score, 0) / results.length * 10) / 10
    : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="mb-6 lg:mb-[30px]">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1.5 tracking-tight text-text-primary">
          {t('algoAnalyzer.title')}
        </h1>
        <p className="text-text-muted text-sm sm:text-[15px] leading-relaxed">
          {t('algoAnalyzer.subtitle')}
        </p>
      </div>

      {/* Input Section */}
      <div className="mb-8 lg:mb-10">
        <label className="block text-base font-bold mb-3 text-text-primary">
          {t('algoAnalyzer.inputLabel')}
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t('algoAnalyzer.placeholder')}
          className="w-full min-h-[180px] p-4 lg:p-5 bg-surface-2 border border-border rounded-2xl text-text-primary text-base resize-y font-[inherit] leading-[1.6] placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        />

        <button
          onClick={handleAnalyze}
          disabled={!content.trim() || isAnalyzing}
          className={cn(
            'mt-4 lg:mt-5 px-10 py-4 border-none rounded-3xl text-base font-bold transition-all',
            !content.trim() || isAnalyzing
              ? 'bg-surface-3 text-text-faint cursor-not-allowed'
              : 'bg-gradient-to-r from-accent to-purple-600 text-white cursor-pointer hover:opacity-90 hover:-translate-y-0.5 shadow-glow-accent hover:shadow-[0_0_30px_rgba(139,92,246,0.25)]'
          )}
        >
          {isAnalyzing ? t('common.analyzing') : t('algoAnalyzer.analyzePost')}
        </button>
      </div>

      {/* Results Section */}
      {results.length > 0 && (
        <div>
          <h2 className="text-xl lg:text-2xl mb-5 text-text-primary">
            {t('algoAnalyzer.results')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
            {results.map((result, index) => (
              <div
                key={index}
                className="p-5 lg:p-6 bg-surface-2 border border-border rounded-2xl transition-all duration-200 hover:border-accent hover:shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:-translate-y-1"
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="w-8 h-8 rounded-lg bg-accent/10 text-accent text-xs font-bold flex items-center justify-center flex-shrink-0">{result.icon}</span>
                  <h3 className="text-lg m-0 text-text-primary font-bold">{result.name}</h3>
                </div>

                <div className="mb-3">
                  <span className={cn(
                    'inline-block px-4 py-2 rounded-xl text-lg font-bold text-white',
                    result.score >= 8
                      ? 'bg-gradient-to-r from-success to-emerald-400'
                      : result.score >= 6
                        ? 'bg-gradient-to-r from-accent to-purple-600'
                        : 'bg-gradient-to-r from-warning to-orange-500'
                  )}>
                    {result.score}/10
                  </span>
                </div>

                <p className="text-sm leading-[1.6] m-0 text-text-muted">
                  {result.comment}
                </p>
              </div>
            ))}
          </div>

          {/* Overall Score */}
          <div className="mt-8 lg:mt-10 p-6 lg:p-8 bg-gradient-to-r from-accent to-purple-600 rounded-2xl text-center">
            <h3 className="text-xl mb-2 text-white">{t('algoAnalyzer.overallScore')}</h3>
            <div className="text-5xl font-bold text-white">{overallScore}/10</div>
            <p className="text-base mt-3 text-white/80">
              {overallScore >= 8
                ? t('algoAnalyzer.excellent')
                : overallScore >= 6
                  ? t('algoAnalyzer.good')
                  : t('algoAnalyzer.needsWork')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
